import React, { useEffect, useRef } from "react";
import { Facebook, Instagram, Linkedin, Mail } from "lucide-react";

/* Official X (formerly Twitter) brand logo — inline SVG */
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/* Official TikTok brand logo — inline SVG */
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.31a8.16 8.16 0 0 0 4.77 1.52V7.37a4.85 4.85 0 0 1-1-.68z" />
  </svg>
);
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { trackAndNavigate } from "../utils/navigate";

const Footer = () => {
  const cqcRef = useRef(null);

  useEffect(() => {
    const container = cqcRef.current;
    if (!container) return;
    const existing = container.querySelector("script[data-cqc]");
    if (existing) return;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src =
      "https://www.cqc.org.uk/sites/all/modules/custom/cqc_widget/widget.js?data-id=1-16309714737&data-host=https://www.cqc.org.uk&type=location";
    script.setAttribute("data-cqc", "true");
    script.async = true;
    container.appendChild(script);
  }, []);

  return (
    <footer className="bg-[#012d5c] text-white pt-20 pb-16 md:pb-10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-8 lg:gap-y-4 mb-10">

          {/* Column 1: Brand */}
          <div>
            <div className="mb-6 bg-white/10 p-2 rounded-lg inline-block">
              <img
                src={`${process.env.PUBLIC_URL}/integro-logo.png`}
                alt="Integro Clinics"
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Providing safe, legal access to medical cannabis for patients across the UK.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.facebook.com/integroclinics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="bg-white/10 p-2 rounded-lg hover:bg-[#4caf50] transition-colors flex items-center justify-center w-9 h-9"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/integroclinics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="bg-white/10 p-2 rounded-lg hover:bg-[#4caf50] transition-colors flex items-center justify-center w-9 h-9"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/integro-medical-clinics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="bg-white/10 p-2 rounded-lg hover:bg-[#4caf50] transition-colors flex items-center justify-center w-9 h-9"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://x.com/ClinicsIntegro"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="bg-white/10 p-2 rounded-lg hover:bg-[#4caf50] transition-colors flex items-center justify-center w-9 h-9"
              >
                <XIcon className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@integroclinics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="bg-white/10 p-2 rounded-lg hover:bg-[#4caf50] transition-colors flex items-center justify-center w-9 h-9"
              >
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2: Main Navigation */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#8ac2f3] mb-5">Navigation</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm">About Us</Link></li>
              <li><Link to="/how-it-works" className="text-gray-300 hover:text-white transition-colors text-sm">How It Works</Link></li>
              <li><Link to="/conditions/back-pain" className="text-gray-300 hover:text-white transition-colors text-sm">Conditions</Link></li>
              <li><Link to="/transfer-clinic" className="text-gray-300 hover:text-white transition-colors text-sm">Transfer Clinic</Link></li>
              <li><Link to="/faq" className="text-gray-300 hover:text-white transition-colors text-sm">FAQs</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-white transition-colors text-sm">Blog</Link></li>
              <li className="pt-2 border-t border-white/10">
                <a
                  href="https://app.integroclinics.com/v2/registration"
                  onClick={(e) => { e.preventDefault(); trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' }); }}
                  className="text-[#4caf50] hover:text-white transition-colors text-sm font-semibold"
                >
                  Check Your Eligibility
                </a>
              </li>
              <li>
                <a
                  href="https://app.integroclinics.com/login"
                  onClick={(e) => { e.preventDefault(); trackAndNavigate("https://app.integroclinics.com/login", 'login_click', { entry_point: 'login' }); }}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Patient Login
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal & Information */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#8ac2f3] mb-5">Legal &amp; Information</h3>
            <ul className="space-y-3">
              <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors text-sm">Privacy Policy</Link></li>
              <li><Link to="/zero-tolerance-policy" className="text-gray-300 hover:text-white transition-colors text-sm">Zero Tolerance Policy</Link></li>
              <li><Link to="/accessibility-statement" className="text-gray-300 hover:text-white transition-colors text-sm">Accessibility Statement</Link></li>
              <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">Terms and Conditions</Link></li>
              <li><Link to="/complaints" className="text-gray-300 hover:text-white transition-colors text-sm">Complaints</Link></li>
              <li><Link to="/patient-rejoining-policy" className="text-gray-300 hover:text-white transition-colors text-sm">Patient Rejoining Policy</Link></li>
              <li><Link to="/sitemap" className="text-gray-300 hover:text-white transition-colors text-sm">Sitemap</Link></li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("openCookieSettings"))}
                  className="text-gray-300 hover:text-white transition-colors text-sm text-left"
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact + CTA */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#8ac2f3] mb-5">Contact Us</h3>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center text-gray-300">
                <Mail className="w-5 h-5 mr-3 text-[#4caf50] shrink-0" />
                <a href="mailto:office@integroclinics.com" className="hover:text-white text-sm transition-colors">
                  office@integroclinics.com
                </a>
              </li>
              <li className="text-gray-300 text-sm">
                <span className="block font-semibold text-white mb-1">Registered Office:</span>
                41 Central Avenue, West Molesey,<br />Surrey, KT8 2QZ
              </li>
              <li className="text-gray-400 text-xs">
                Monday – Friday, 9am – 5pm
              </li>
            </ul>

            <Button
              className="w-full bg-[#4caf50] hover:bg-[#3d8b40] text-white rounded-lg py-5 text-base font-bold shadow-lg shadow-[#4caf50]/20"
              onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
            >
              Check Eligibility
            </Button>
          </div>

          {/* CQC widget — spans columns 3 & 4 */}
          <div className="md:col-span-2 lg:col-start-3 lg:col-span-2 overflow-hidden">
            <div
              ref={cqcRef}
              aria-label="Care Quality Commission registration"
              style={{ zoom: "0.85", maxWidth: "100%" }}
            />
          </div>

        </div>

        <div className="border-t border-white/10 mb-6" />

        {/* Clinical information note */}
        <p className="text-xs text-gray-500 leading-relaxed mb-6">
          Information on this website is provided for general educational purposes only and does not constitute medical advice. Eligibility for treatment is determined individually by our clinical team following a clinical assessment. A consultation or eligibility check does not guarantee that a prescription will be offered.
        </p>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-400">
          <p>© {new Date().getFullYear()} Integro Clinics. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms and Conditions</Link>
            <Link to="/complaints" className="hover:text-white transition-colors">Complaints</Link>
            <Link to="/sitemap" className="hover:text-white transition-colors">Sitemap</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
